import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { WordsState, WordsActions } from '../types';
import { getApiService } from '../api/services';

interface WordsStore extends WordsState {
  actions: WordsActions;
}

export const useWordsStore = create<WordsStore>()(
  devtools(
    set => ({
      // Initial state
      words: [],
      collections: {
        history: [],
        current: null,
      },
      isLoading: false,
      error: null,

      // Actions
      actions: {
        loadWords: async (query = '') => {
          try {
            set(
              { isLoading: true, error: null },
              false,
              'words/loadWords/start'
            );

            const apiService = getApiService();
            const words = await apiService.getWords(query ? `?${query}` : '');

            set(
              {
                words: words.items,
                isLoading: false,
                error: null,
              },
              false,
              'words/loadWords/success'
            );
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : 'Failed to load words';
            set(
              {
                isLoading: false,
                error: errorMessage,
              },
              false,
              'words/loadWords/error'
            );
            throw error;
          }
        },

        loadCollectionHistory: async () => {
          try {
            set(
              { isLoading: true, error: null },
              false,
              'words/loadCollectionHistory/start'
            );

            const apiService = getApiService();
            const history = await apiService.getLearnHistory();

            set(
              state => ({
                collections: {
                  ...state.collections,
                  history,
                },
                isLoading: false,
                error: null,
              }),
              false,
              'words/loadCollectionHistory/success'
            );
          } catch (error) {
            const errorMessage =
              error instanceof Error
                ? error.message
                : 'Failed to load collection history';
            set(
              {
                isLoading: false,
                error: errorMessage,
              },
              false,
              'words/loadCollectionHistory/error'
            );
            throw error;
          }
        },

        loadCollection: async (id: string) => {
          try {
            set(
              { isLoading: true, error: null },
              false,
              'words/loadCollection/start'
            );

            const apiService = getApiService();
            const collection = await apiService.getLearnCollection(id);

            set(
              state => ({
                collections: {
                  ...state.collections,
                  current: collection,
                },
                isLoading: false,
                error: null,
              }),
              false,
              'words/loadCollection/success'
            );
          } catch (error) {
            const errorMessage =
              error instanceof Error
                ? error.message
                : 'Failed to load collection';
            set(
              {
                isLoading: false,
                error: errorMessage,
              },
              false,
              'words/loadCollection/error'
            );
            throw error;
          }
        },

        setLoading: (loading: boolean) => {
          set({ isLoading: loading }, false, 'words/setLoading');
        },

        setError: (error: string | null) => {
          set({ error }, false, 'words/setError');
        },
      },
    }),
    {
      name: 'words-store',
    }
  )
);
